import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  CheckCircle, 
  Clock, 
  Users, 
  ArrowRight, 
  Link as LinkIcon,
  Eye,
  UserPlus,
  Calendar,
  AlertCircle
} from "lucide-react";
import type { FlowResult } from "./ChallengeJoinFlow";

interface ConfirmationStepProps {
  result: FlowResult;
  challengeTitle: string;
  onComplete: () => void;
  onBrowseOtherTeams: () => void;
  // Event registration props
  eventId?: string | null;
  eventName?: string;
  eventSlug?: string;
  isRegisteredForEvent?: boolean;
  onInviteTeammates?: () => void;
}

const ConfirmationStep: React.FC<ConfirmationStepProps> = ({
  result,
  challengeTitle,
  onComplete,
  onBrowseOtherTeams,
  onInviteTeammates,
  eventId,
  eventName,
  eventSlug,
  isRegisteredForEvent = true,
}) => {
  const isCreatedTeam = result.action === "created_team";
  const isAlreadyOnTeam = result.action === "already_on_team";
  const isSuccess = isCreatedTeam || isAlreadyOnTeam;

  return (
    <div className="py-4">
      <Card className={`border-2 ${isSuccess ? "border-green-500/30 bg-green-500/5" : "border-amber-500/30 bg-amber-500/5"}`}>
        <CardContent className="p-6 text-center">
          {/* Icon */}
          <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${isSuccess ? "bg-green-500/20" : "bg-amber-500/20"}`}>
            {isSuccess ? (
              <CheckCircle className="h-8 w-8 text-green-600" />
            ) : (
              <Clock className="h-8 w-8 text-amber-600" />
            )}
          </div>

          {/* Title */}
          <h3 className="text-xl font-bold text-foreground mb-2">
            {isCreatedTeam 
              ? "Team Created!" 
              : isAlreadyOnTeam 
              ? "You're on a Team!" 
              : "Request Sent"}
          </h3>

          {/* Message */}
          <p className="text-muted-foreground mb-6">
            {isCreatedTeam ? (
              <>
                You're now the lead of <span className="font-medium text-foreground">"{result.teamName}"</span>
              </>
            ) : isAlreadyOnTeam ? (
              <>
                You're already a member of <span className="font-medium text-foreground">"{result.teamName}"</span>. You're all set!
              </>
            ) : (
              <>
                Your request to join <span className="font-medium text-foreground">"{result.teamName}"</span> has been sent to the team lead.
              </>
            )}
          </p>

          {/* Event Registration CTA - shown prominently if not registered */}
          {eventId && !isRegisteredForEvent && eventSlug && (
            <div className="bg-amber-50 dark:bg-amber-950/30 border-2 border-amber-300 dark:border-amber-700 rounded-lg p-4 mb-6 text-left">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-500/20">
                  <AlertCircle className="h-5 w-5 text-amber-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-amber-800 dark:text-amber-200">
                    Important: Register for the Event
                  </p>
                  <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                    This challenge is part of <strong>{eventName}</strong>. You must register to participate in person.
                  </p>
                  <Button 
                    asChild 
                    size="sm" 
                    className="mt-3 bg-amber-600 hover:bg-amber-700 text-white"
                  >
                    <Link to={`/events/${eventSlug}`}>
                      <Calendar className="mr-2 h-4 w-4" />
                      Register for Event
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* What's Next */}
          <div className="bg-background/50 rounded-lg p-4 mb-6 text-left">
            <h4 className="font-medium text-foreground mb-3">What's Next:</h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              {isCreatedTeam ? (
                <>
                  <li className="flex items-start gap-2">
                    <Users className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Share your team with others to recruit members</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Eye className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Review the challenge brief and start planning</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <LinkIcon className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Check your dashboard to manage join requests</span>
                  </li>
                </>
              ) : isAlreadyOnTeam ? (
                <>
                  <li className="flex items-start gap-2">
                    <Users className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>View your team's details in the dashboard</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Eye className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Review the challenge brief and start planning</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <LinkIcon className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>Connect with your team members</span>
                  </li>
                </>
              ) : (
                <>
                  <li className="flex items-start gap-2">
                    <Clock className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>The team lead will review your request</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>You'll receive a notification when they respond</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Users className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <span>You can browse other teams while waiting</span>
                  </li>
                </>
              )}
            </ul>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Show Invite Teammates as primary CTA for team creators/members */}
            {isSuccess && onInviteTeammates && (
              <Button onClick={onInviteTeammates} className="flex-1 gap-2">
                <UserPlus className="h-4 w-4" />
                Invite Teammates
              </Button>
            )}
            <Button 
              onClick={onComplete} 
              variant={isSuccess && onInviteTeammates ? "outline" : "default"}
              className="flex-1 gap-2"
            >
              {isSuccess && onInviteTeammates ? "Skip to Dashboard" : "Go to Dashboard"}
              <ArrowRight className="h-4 w-4" />
            </Button>
            {!isSuccess && (
              <Button variant="outline" onClick={onBrowseOtherTeams} className="flex-1">
                Browse Other Teams
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ConfirmationStep;
